// src/pages/user/BulkReview.js
import React, { useEffect, useState } from "react";
import UserLayout from "../../components/UserLayout";
import { Button } from "../../components/ui/button";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import BulkReviewDrawer from "../../components/BulkReviewDrawer";
import BulkSubmitWarningModal from "../../components/BulkSubmitWarningModal";
import { useToast } from "../../contexts/ToastContext";   // ✅ Add Toast System

export default function BulkReview() {
  const navigate = useNavigate();
  const { showToast } = useToast();  // ✅ Toast hook
  const [searchParams] = useSearchParams();
  const batchId = searchParams.get("batch");

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);   // ✅ New state

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [warningOpen, setWarningOpen] = useState(false);

  useEffect(() => {
    if (!batchId) {
      setLoading(false);
      return;
    }
    fetchApplications();
  }, [batchId]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/bulk/${batchId}/applications`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const apps = (res.data.applications || []).map((app) => ({
        ...app,
        reviewed: Boolean(app.reviewed) || false,
      }));

      setApplications(apps);
    } catch (err) {
      console.error("Failed to load bulk apps", err);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = (app) => {
    setSelectedApp(app);
    setDrawerOpen(true);
  };

  const markReviewed = (applicationId) => {
    setApplications((prev) =>
      prev.map((a) => (a.id === applicationId ? { ...a, reviewed: true } : a))
    );
    setDrawerOpen(false);
    setSelectedApp(null);
  };

  const handleSubmitAll = () => {
    const reviewedCount = applications.filter((a) => a.reviewed).length;
    if (reviewedCount !== applications.length) {
      setWarningOpen(true);
      return;
    }
    submitBulk();
  };

  // ✅ FINAL LOGIC — Replaces alert(); adds loader + success toast
  const submitBulk = async () => {
    try {
      setSubmitting(true);

      const token = localStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/bulk/${batchId}/submit`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // 🎉 Custom DU Global Toast
      showToast("All applications submitted successfully!", "success");

      // 🔄 redirect after a small pause (so toast displays)
      setTimeout(() => navigate("/dashboard"), 1000);

    } catch (err) {
      console.error("Submit failed", err);
      showToast("Submission failed. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (!batchId) {
    return (
      <UserLayout>
        <div className="pt-24 max-w-4xl mx-auto text-center text-gray-600">
          ❌ Invalid bulk session. Missing batch ID.
        </div>
      </UserLayout>
    );
  }

  if (loading) {
    return (
      <UserLayout>
        <div className="pt-24 max-w-4xl mx-auto text-center text-gray-600">
          Loading applications…
        </div>
      </UserLayout>
    );
  }

  if (applications.length === 0) {
    return (
      <UserLayout>
        <div className="pt-24 max-w-4xl mx-auto text-center text-gray-600">
          No applications found for this batch.
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="pt-24 max-w-4xl mx-auto space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Bulk Application Review
          </h2>
          <p className="text-gray-600 mt-1">
            Review each application before final submission.
          </p>
        </div>

        <div className="space-y-4">
          {applications.map((app) => {
            const mapped =
            app.extracted_fields ||
            app.extracted?.mapped ||
            (app.passengers?.[0] || {});
            const extractedCount = Object.values(mapped).filter(
              (v) => v !== null && v !== undefined && String(v).trim() !== ""
            ).length;

            const passportNo =
              mapped["Passport Number"] ||
              mapped.passport_number ||
              "—";

            return (
              <div
                key={app.id}
                className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm hover:shadow-md transition-all"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {app.applicant_name ||
                        app.passengers?.[0]?.full_name ||
                        "Unknown Applicant"}
                    </h3>

                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Application ID:</span>{" "}
                      {app.id}
                    </p>

                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Passport:</span>{" "}
                      {passportNo}
                    </p>

                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Fields Extracted:</span>{" "}
                      {extractedCount}
                    </p>
                  </div>

                  <div className="flex flex-col items-end">
                    {app.reviewed ? (
                      <span className="px-2 py-1 text-xs rounded-md bg-green-100 text-green-700 border border-green-200 mb-2">
                        Reviewed ✓
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs rounded-md bg-yellow-100 text-yellow-700 border border-yellow-200 mb-2">
                        Not Reviewed
                      </span>
                    )}

                    <Button
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => handleReview(app)}
                    >
                      Review
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ------------------ Submit Button ------------------ */}
        <Button
          className="bg-green-600 hover:bg-green-700 text-white w-full py-3 text-lg flex items-center justify-center gap-2"
          onClick={handleSubmitAll}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 
                  0 5.373 0 12h4zm2 5.291A7.962 
                  7.962 0 014 12H0c0 3.042 1.135 
                  5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Submitting…
            </>
          ) : (
            "Submit All Applications"
          )}
        </Button>
      </div>

      {/* Drawer & Warning Modal */}
      <BulkReviewDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedApp(null);
        }}
        app={selectedApp}
        markReviewed={markReviewed}
        batchId={batchId}
      />

      <BulkSubmitWarningModal
        open={warningOpen}
        onClose={() => setWarningOpen(false)}
        applications={applications}
        onConfirm={() => {
          setWarningOpen(false);
          submitBulk();
        }}
      />
    </UserLayout>
  );
}